'use client';

import Link from 'next/link';
import Image from 'next/image';
import { MapPin, Phone, Mail } from 'lucide-react';
import siteTheme from '@/lib/theme';
import { parseMarkdown } from '@/lib/markdown';
import type { SiteSettings } from '@/lib/cms/settings';
import type { CmsFooter } from '@/lib/cms/layout';
const { colors, fonts } = siteTheme;

/* ── Social icons ── */
const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  facebook: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
    </svg>
  ),
  instagram: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  youtube: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
    </svg>
  ),
  whatsapp: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
  twitter: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  linkedin: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  ),
  tripadvisor: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z" />
    </svg>
  ),
};

/* ── Compass divider SVG ── */
function CompassDivider({ size = 28, color = 'currentColor' }: { size?: number; color?: string }) {
  return (

    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 222.75 160.5"
      fill="none"
    >
      <path
        d="M111.4 28c-12 18-18 38-18 58 0 20 8 37 18 48 10-11 18-28 18-48 0-20-6-40-18-58Z"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M73 64c-20 6-34 18-42 34 18 0 34-4 48-12 10-6 18-14 24-24-10-2-20-1-30 2Z"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M149 64c20 6 34 18 42 34-18 0-34-4-48-12-10-6-18-14-24-24 10-2 20-1 30 2Z"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M88 58c-10 10-16 24-16 40 0 10 2 20 6 28 8-8 14-18 18-30 4-12 5-24 2-38-4 0-7 0-10 0Z"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M134 58c10 10 16 24 16 40 0 10-2 20-6 28-8-8-14-18-18-30-4-12-5-24-2-38 4 0 7 0 10 0Z"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M82 118c8 6 18 10 29 10s21-4 29-10"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  )};



  export default function FooterV2({
    settings,
    footer,
  }: {
    settings?: SiteSettings | null;
    footer?: CmsFooter | null;
  }) {
    const siteName = settings?.site_name ?? 'Sandy Resort';
    const socialLinks = footer?.col_social?.social_links ?? [];
    const navLinks = footer?.col_social?.nav_links ?? [];
    const footerLogo = footer?.col_brand?.logo ?? null;
    const address = footer?.col_contact?.address ?? null;
    const phones = footer?.col_contact?.phones ?? [];
    const emails = footer?.col_contact?.emails ?? [];
    const policyLinks = footer?.bottom_bar?.links ?? [];
    const copyright = footer?.bottom_bar?.copyright_text
      ?? `© ${new Date().getFullYear()} ${siteName}. All Rights Reserved.`;
    return (
      <footer className="ftv2-root">

        {/* ── Decorative background tints ── */}
        <div className="ftv2-deco-tl" aria-hidden="true" >
          {footerLogo && (
            <Image
              src={'https://cms.sandyresort.in/uploads/sandy_leaf_330420605e.png'}
              alt=""
              fill
              sizes="315px"
              style={{ objectFit: 'contain', objectPosition: 'center' }}
            />
          )}
        </div>
        <div className="ftv2-deco-br" aria-hidden="true">
          {footerLogo && (
            <Image
              src={'https://cms.sandyresort.in/uploads/sandy_peacock_df0a09f5a3.png'}
              alt=""
              fill
              sizes="315px"
              style={{ objectFit: 'contain', objectPosition: 'center' }}
            />
          )}
        </div>

        {/* ══════════════════════════════════════════
          MAIN SECTION — 3-column grid
      ══════════════════════════════════════════ */}
        <section className="ftv2-main">
          <div className="mx-auto grid max-w-[1480px] grid-cols-1 gap-14 md:gap-16 lg:grid-cols-12 lg:items-center">

            {/* ── Left: FOLLOW US + social ── */}
            <div className="order-2 flex flex-col items-center space-y-6 text-center lg:order-1 lg:col-span-3 lg:items-start lg:text-left">
              <p className="section-label" style={{ color: colors.gold }}>Follow Us</p>
              <div className="flex items-center gap-4">
                {socialLinks.length > 0 ? (
                  socialLinks.map(s => (
                    <a
                      key={s.id}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label || s.platform}
                      className="ftv2-social-btn footer-social"
                    >
                      {SOCIAL_ICONS[s.platform.toLowerCase()] ?? SOCIAL_ICONS.instagram}
                    </a>
                  ))
                ) : (
                  /* Fallback placeholder circles */
                  ['Facebook', 'Instagram'].map(p => (
                    <div key={p} className="ftv2-social-btn" aria-label={p}>
                      {SOCIAL_ICONS[p.toLowerCase()]}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ── Center: Logo + divider + nav links ── */}
            <div className="order-1 flex flex-col items-center px-2 sm:px-6 lg:order-2 lg:col-span-6 lg:border-l lg:border-r lg:border-[--gold-pale] lg:px-12">

              {/* Logo */}
              {footerLogo ? (
                <div className="relative mb-6 h-16 w-48 sm:h-20">
                  <Image
                    src={footerLogo.url}
                    alt={siteName}
                    fill
                    sizes="240px"
                    style={{ objectFit: 'contain' }}
                  />
                </div>
              ) : (
                <p className="mb-6" style={{ fontFamily: fonts.serif, fontSize: '3.6rem', fontWeight: 300, letterSpacing: '0.08em', color: colors.gold }}>{siteName}</p>
              )}

              {/* Gold rule + compass divider */}
              <div className="ftv2-divider-row">
                <span className="ftv2-divider-line ftv2-divider-line--right" />
                <span style={{ color: colors.gold }}><CompassDivider /></span>
                <span className="ftv2-divider-line ftv2-divider-line--left" />
              </div>

              {/* Primary nav links */}
              {navLinks.length > 0 && (
                <nav className="mt-6 flex flex-wrap items-center justify-center gap-y-3 text-center">
                  {navLinks.map((link, i) => (
                    <div key={link.id} className="flex items-center">
                      {i !== 0 && <div className="ftv2-nav-sep" />}
                      <Link href={link.href} className="ftv2-nav-link">
                        {link.label}
                      </Link>
                    </div>
                  ))}
                </nav>
              )}
            </div>

            {/* ── Right: Contact info ── */}
            <div className="order-3 flex flex-col items-center space-y-5 text-center lg:col-span-3 lg:items-start lg:pl-4 lg:text-left">

              <h3 className="section-label" style={{ color: colors.gold }}>{siteName}</h3>

              {/* Address */}
              {address && (
                <div className="ftv2-contact-row justify-center lg:justify-start">
                  <MapPin size={18} strokeWidth={1.8} style={{ color: colors.gold, flexShrink: 0, marginTop: '0.1rem' }} />
                  <div
                    className="ftv2-contact-text"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(address) }}
                  />
                </div>
              )}

              {/* Divider rule */}
              {address && (phones.length > 0 || emails.length > 0) && (
                <div style={{ width: '4rem', height: '1px', background: `${colors.goldPale}` }} />
              )}

              {/* Phone */}
              {phones.map(p => (
                <div key={p.id} className="ftv2-contact-row justify-center lg:justify-start">
                  <Phone size={18} strokeWidth={1.8} style={{ color: colors.gold, flexShrink: 0 }} />
                  <a href={`tel:${p.number.replace(/\\s+/g, '')}`} className="ftv2-contact-text ftv2-contact-link">{p.number}</a>
                </div>
              ))}

              {/* Email */}
              {emails.map(e => (
                <div key={e.id} className="ftv2-contact-row justify-center lg:justify-start">
                  <Mail size={18} strokeWidth={1.8} style={{ color: colors.gold, flexShrink: 0 }} />
                  <a href={`mailto:${e.email}`} className="ftv2-contact-text ftv2-contact-link">{e.email}</a>
                </div>
              ))}

            </div>

          </div>
        </section>

        {/* ══════════════════════════════════════════
          BOTTOM DARK BAR
      ══════════════════════════════════════════ */}
        <section className="ftv2-bottom">

          {/* Background texture overlay */}
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
            <div style={{ width: '100%', height: '100%', backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0, rgba(255,255,255,0.03) 1px, transparent 0, transparent 50%)', backgroundSize: '8px 8px' }} />
          </div>

          <div className="relative mx-auto flex max-w-[1480px] flex-col items-center">

            {/* Policy links */}
            {policyLinks.length > 0 && (
              <nav className="mb-8 flex flex-wrap items-center justify-center gap-y-3 text-center">
                {policyLinks.map((link, i) => (
                  <div key={link.id} className="flex items-center">
                    {i !== 0 && <div className="ftv2-policy-sep" />}
                    <Link href={link.href} className="ftv2-policy-link">
                      {link.label}
                    </Link>
                  </div>
                ))}
              </nav>
            )}



            {/* Gold rule + compass divider */}
            <div className="ftv2-divider-row">
              <span className="ftv2-divider-line ftv2-divider-line--right" />
              <span style={{ color: colors.gold }}><CompassDivider /></span>
              <span className="ftv2-divider-line ftv2-divider-line--left" />
            </div>

            {/* Copyright + powered by */}
            <div className="flex flex-col items-center gap-2 px-4 text-center md:flex-row md:gap-3">
              <div
                className="ftv2-copyright"
                dangerouslySetInnerHTML={{ __html: copyright }}
              />
              <span className="ftv2-copyright-sep hidden md:block">|</span>
              <p className="ftv2-copyright">
                Powered by{' '}
                <a
                  href="https://qlix.digital"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--green)', textDecoration: 'none' }}
                >
                  Qlix Digital
                </a>
              </p>
            </div>

          </div>
        </section>

      </footer>
    );
  }
